import React, {useEffect} from "react";
import {Outlet} from "react-router";
import styled from "styled-components";
import {NavLink, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Advert, HOCLayout, Preloader} from "components";
import {media} from "assets/css/media";
import {useDispatch, useSelector} from "react-redux";
import {getJsonState} from "modules/selectors";
import {RequestState, trackAdobeAnalytics} from "modules";
import {fetchHelpPageJsons} from "modules/actions";

const Wrapper = styled.div`
	background: linear-gradient(180deg, #03122b 0%, #7800ff 100%);
	padding: 24px;
	position: relative;

	@media (min-width: ${media.mobile}) {
		padding: 50px 70px;
	}
`;

const NavigationBlock = styled.div`
	height: 100%;
	min-width: 125px;
	margin-right: 8px;
	position: sticky;
	top: 150px;

	ul {
		height: 100%;
		list-style: decimal inside;
		display: block;

		li {
			font-family: "SF Pro Text", sans-serif;
			font-style: normal;
			font-weight: normal;
			font-size: 18px;
			line-height: 46px;
			color: #ffffff;
		}

		a {
			transition: color 0.25s;

			&:hover {
				color: #f1db16;
			}

			&.active {
				color: #f1db16;
				font-weight: bold;
			}
		}
	}

	@media (max-width: 480px) {
		ul li a {
			font-size: 14px;
		}
	}

	@media (max-width: ${media.mobile}) {
		top: 0;

		ul {
			display: flex;
			list-style: none;

			li {
				width: 100%;
				text-align: left;
				margin-right: 27px;
				line-height: normal;

				a {
					display: block;
					width: 100%;
					padding-bottom: 5px;

					&.active {
						color: #f1db16;
						border-bottom: 3px solid #f1db16;
					}
				}

				&:last-child {
					max-width: 50px;
					margin-right: 0;
				}
			}
		}
	}

	@media (min-width: ${media.mobile}) {
		margin-right: 16px;
	}
`;

const Content = styled.div`
	margin-top: 20px;

	@media (min-width: ${media.mobile}) {
		display: flex;
		margin-top: 50px;
	}
`;

const Title = styled.h1`
	font-size: 24px;
	font-weight: 600;
	color: #ffffff;
	text-shadow: 0 2px 2px rgba(0, 0, 0, 0.5);

	@media (min-width: ${media.mobile}) {
		font-size: 48px;
	}
`;

const OutletWrapper = styled.div`
	width: 100%;
	max-width: 640px;
	margin: 13px auto 0;

	@media (max-width: ${media.mobile}) {
		margin-top: 24px;
	}
`;

interface IHelpLayout {
	links: Array<{url: string; key: string}>;
}

export const HelpLayout: React.FC<IHelpLayout> = ({links}) => {
	const {t} = useTranslation();
	const dispatch = useDispatch();
	const {requestState, faqRequestState} = useSelector(getJsonState);
	const isLoading =
		requestState === RequestState.Requested || faqRequestState === RequestState.Requested;
	const {pathname} = useLocation();

	useEffect(() => {
		dispatch(fetchHelpPageJsons());
	}, [dispatch]);

	useEffect(() => {
		const section = `:${pathname.split("/").pop() || ""}`;

		trackAdobeAnalytics({
			section,
		});
	}, [pathname]);

	return (
		<HOCLayout>
			<Wrapper>
				<Advert hide={true} />
				<Title>{t("help.fifa.title")}</Title>
				<Content>
					<NavigationBlock>
						<ul>
							{links.map(({url, key}) => (
								<li key={url}>
									<NavLink to={url}>{t(key)}</NavLink>
								</li>
							))}
						</ul>
					</NavigationBlock>
					<OutletWrapper>{isLoading ? <Preloader /> : <Outlet />}</OutletWrapper>
				</Content>
			</Wrapper>
		</HOCLayout>
	);
};
